import { Component, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Store } from '@ngrx/store';
import { take } from 'rxjs/operators';

import { CheckoutActions } from '../../actions';
import { State } from '../../reducers';
import * as OrderApproveSelectors from '../../reducers/order-approve.reducer';
import * as ValidationPatterns from '../../configurations/validations';
import { CheckoutFacade } from '../../facades/checkout.facade';
import { IPointOfContact } from 'src/app/models/common.models';
import { ICheckoutUpdate } from '../../models/checkout.models';
import { CustomerFacade } from '../../facades/customer.facade';
import { AppUtils } from '../../utils/app.utils';
import { EStoreTypes } from '../../configurations/common';

@Component({
  selector: 'app-delivery-instructions-modal',
  templateUrl: './delivery-instructions-modal.component.html',
  styleUrls: ['./delivery-instructions-modal.component.scss'],
  standalone: false,
})
export class DeliveryInstructionsModalComponent implements OnInit {
  @Input() showModal!: boolean;
  @Input() isSaturdayShipment: boolean = false;

  @Output() closeModal: EventEmitter<any> = new EventEmitter<any>();
  @Output() reloadCheckoutData: EventEmitter<any> = new EventEmitter<any>();

  protected readonly AppUtils = AppUtils;

  isUsStore: boolean = false;
  cartId!: string;
  pocForm!: UntypedFormGroup;
  pocData!: IPointOfContact;
  updatePOCDataIsInProgress: boolean = false;

  maxLengthPhone: number = 20;
  maxLengthName: number = 35;
  maxLengthComment: number = 1000;
  maxLengthAttentionTo: number = 35;

  private readonly store: Store<State> = inject(Store<State>);
  private readonly formBuilder: UntypedFormBuilder = inject(UntypedFormBuilder);
  private readonly checkoutFacade: CheckoutFacade = inject(CheckoutFacade);
  private readonly customerFacade: CustomerFacade = inject(CustomerFacade);

  ngOnInit(): void {
    this.isUsStore = AppUtils.isStoreActive(EStoreTypes.US);
    this.getCartId();
  }

  formIsValid(): boolean {
    return this.pocForm.status === 'VALID';
  }

  resetForm(): void {
    this.pocForm.reset();
    this.closeModal.emit();
  }

  initializeForm(): void {
    this.pocForm = this.formBuilder.group({
      attentionTo: [this.pocData.attentionTo, [Validators.maxLength(this.maxLengthAttentionTo)]],
      firstName: [this.pocData.firstName, [Validators.required, Validators.maxLength(this.maxLengthName), ValidationPatterns.onlyLetters]],
      lastName: [this.pocData.lastName, [Validators.required, Validators.maxLength(this.maxLengthName), ValidationPatterns.onlyLetters]],
      email: [this.pocData.email, [Validators.required, Validators.email]],
      phone: [this.pocData.phone, [Validators.required, Validators.maxLength(this.maxLengthPhone), ValidationPatterns.phonePattern]],
      comment: [this.pocData.comment, [Validators.maxLength(this.maxLengthComment), ValidationPatterns.noEmptySpaceOnTheBeginning]],
    });
  }

  createPointOfContactFromFormValues(): IPointOfContact {
    return {
      attentionTo: this.pocForm.value.attentionTo,
      firstName: this.pocForm.value.firstName,
      lastName: this.pocForm.value.lastName,
      email: this.pocForm.value.email,
      phone: this.pocForm.value.phone,
      comment: this.pocForm.value.comment,
    };
  }

  updatePOCData(): void {
    if (this.formIsValid()) {
      this.updatePOCDataIsInProgress = true;

      const checkoutUpdate: ICheckoutUpdate = {
        data: {
          type: 'checkout-update',
          attributes: {
            idCart: this.cartId,
            isAddressSavingSkipped: true,
            pointOfContact: {
              pointOfContactId: 'pointOfContact',
              attentionTo: this.pocForm.value.attentionTo,
              firstName: this.pocForm.value.firstName,
              lastName: this.pocForm.value.lastName,
              email: this.pocForm.value.email,
              phone: this.pocForm.value.phone,
              comment: this.pocForm.value.comment,
            },
          },
        },
      };

      this.checkoutFacade.postCheckoutUpdate(checkoutUpdate).pipe(
        take(1),
      ).subscribe({
        next: _ => {
          this.store.dispatch(CheckoutActions.UpdatePOCData({
            payload: {
              attentionTo: this.pocForm.value.attentionTo,
              firstName: this.pocForm.value.firstName,
              lastName: this.pocForm.value.lastName,
              email: this.pocForm.value.email,
              phone: this.pocForm.value.phone,
              comment: this.pocForm.value.comment,
            },
          }));

          this.reloadCheckoutData.emit();
          this.closeModal.emit();
          this.updatePOCDataIsInProgress = false;
        },
      });

      this.customerFacade.setPointOfContact(this.createPointOfContactFromFormValues());
    }
  }

  private getCartId(): void {
    this.store.select(OrderApproveSelectors.getCartId)
      .pipe(take(1))
      .subscribe(cartId => {
        this.cartId = cartId;
      });
  }
}
